import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import ZaikokanrisihyoPage from './pages/ZaikokanrisihyoPage';
import GetujibunsekiPage from './pages/GetujibunsekiPage';
import { digitalToolsAuth } from './utils/digitalToolsAuth';
import { shouldUseSSOAuth } from './utils/env';
import { setDigitalToolsAuth } from './api/api';

function App() {
  console.log('🚀 App component render:', new Date().toISOString());
  
  // SSO認証を使用するかどうかをuseStateで管理（useEffect内で設定）
  // 初期値はnullにして、設定されるまで待機する
  const [useSSOAuth, setUseSSOAuth] = useState(null);
  const [authState, setAuthState] = useState('checking');
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  
  const digitalToolsAuthRef = useRef(digitalToolsAuth);
  const unsubscribeRef = useRef(null);
  const initializationStartedRef = useRef(false);
  const redirectAttemptedRef = useRef(false);
  
  // 環境情報をログ出力
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'N/A';
  
  console.log('🔍 環境情報:', {
    hostname,
    REACT_APP_ENABLE_SSO_AUTH: process.env.REACT_APP_ENABLE_SSO_AUTH,
    REACT_APP_ENVIRONMENT_MODE: process.env.REACT_APP_ENVIRONMENT_MODE,
    REACT_APP_GOOGLE_CLOUD_PROJECT: process.env.REACT_APP_GOOGLE_CLOUD_PROJECT,
    useSSOAuth,
  });

  // 認証初期化（マウント時のみ実行）
  useEffect(() => {
    // 既に初期化が開始されている場合はスキップ（2重処理を防ぐ）
    if (initializationStartedRef.current) {
      return;
    }
    initializationStartedRef.current = true;

    // shouldUseSSOAuth()を1回だけ呼び出し、その結果をstateに設定
    const shouldUseSSO = shouldUseSSOAuth();
    setUseSSOAuth(shouldUseSSO);

    // API呼び出しヘルパーに認証インスタンスを登録
    setDigitalToolsAuth(digitalToolsAuthRef.current);

    // SSO認証を使用しない環境（開発環境）では認証チェックをスキップ
    if (!shouldUseSSO) {
      console.log('🔓 開発環境: デジタルツール認証チェックをスキップします（認証なしでアクセス可能）');
      setAuthState('authenticated');
      setIsInitialized(true);
      return;
    }

    // digitalToolsAuthが利用可能か確認
    const auth = digitalToolsAuthRef.current;
    if (!auth) {
      console.error('❌ digitalToolsAuthが利用できません');
      setError(new Error('認証モジュールが初期化されていません'));
      setIsInitialized(true);
      return;
    }

    // 認証状態変更ハンドラ
    const handleAuthStateChange = (state) => {
      setAuthState(state);
      console.log('🔐 認証状態変更:', state);
    };

    // SSO認証を使用する環境（検証環境・本番環境）
    // 認証状態の監視を開始
    const unsubscribe = auth.onAuthStateChanged(handleAuthStateChange);
    unsubscribeRef.current = unsubscribe;

    // 初期認証チェック
    const initializeAuth = async () => {
      try {
        console.log('🔄 認証初期化開始...');
        await auth.checkAuth();
        console.log('✅ 認証初期化完了');
        setIsInitialized(true);
        setError(null);
      } catch (error) {
        console.error('❌ 認証初期化エラー:', error);
        if (error instanceof Error) {
          console.error('❌ エラーメッセージ:', error.message);
          console.error('❌ エラースタック:', error.stack);
          setError(error);
        }
        setIsInitialized(true);
      }
    };

    initializeAuth();

    // クリーンアップ
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []); // マウント時のみ実行

  // 未認証の場合はデジタルツールにリダイレクト（一度だけ実行）
  useEffect(() => {
    // SSO認証を使用しない場合はスキップ
    if (!useSSOAuth || authState !== 'unauthenticated' || !isInitialized) {
      return;
    }
    
    // 既にリダイレクトを試みた場合はスキップ（ループ防止）
    if (redirectAttemptedRef.current) {
      return;
    }
    redirectAttemptedRef.current = true;
    
    const auth = digitalToolsAuthRef.current;
    if (!auth) {
      return;
    }
    
    console.log('🔐 未認証状態: デジタルツールにリダイレクトします');
    // 少し遅延を入れてからリダイレクト（ユーザーにメッセージを見せるため）
    const timer = setTimeout(() => {
      auth.redirectToDigitalTools();
    }, 1000);
    return () => clearTimeout(timer);
  }, [useSSOAuth, authState, isInitialized]);

  // エラーが発生した場合の表示
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{ color: 'red', fontWeight: 'bold' }}>認証エラーが発生しました</div>
        <div style={{ color: '#666' }}>{error.message}</div>
        <button 
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          style={{
            padding: '0.5rem 1rem',
            marginTop: '1rem',
            cursor: 'pointer'
          }}
        >
          再読み込み
        </button>
      </div>
    );
  }

  // useSSOAuthがまだ設定されていない場合は待機
  if (useSSOAuth === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>初期化中...</div>
      </div>
    );
  }

  // 認証チェック中はローディング表示（SSO認証を使用する場合のみ）
  if (useSSOAuth && (!isInitialized || authState === 'checking')) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>認証チェック中...</div>
      </div>
    );
  }

  if (useSSOAuth && authState === 'unauthenticated') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>認証が必要です。デジタルツールにリダイレクトしています...</div>
      </div>
    );
  }

  // 認証済みの場合は通常のレイアウトを表示
  return (
    <Router>
      <div className="App">
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-title">
              <h1>センター数値集計ツール</h1>
            </Link>
            <div className="nav-links">
              <Link to="/zaikokanrisihyo" className="nav-link">
                常温在庫管理指標
              </Link>
              <Link to="/getujibunseki" className="nav-link">
                月次分析表
              </Link>
            </div>
          </div>
        </nav>
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/zaikokanrisihyo" element={<ZaikokanrisihyoPage />} />
            <Route path="/getujibunseki" element={<GetujibunsekiPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function HomePage() {
  return (
    <div className="home-page">
      <h2>センター数値集計ツール</h2>
      <div className="home-buttons">
        <Link to="/zaikokanrisihyo" className="home-button">
          常温在庫管理指標<br />バッチ処理
        </Link>
        <Link to="/getujibunseki" className="home-button">
          月次分析表<br />バッチ処理
        </Link>
      </div>
    </div>
  );
}

export default App;
